import { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { TextField } from 'builder/components/TextField'
import { ButtonV2 } from 'builder/components/Button'
import { useI18n } from 'builder/hooks/useI18n'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors } from 'builder/modules/resumeEditor'
import { FetchStatuses } from 'builder/modules/constants'
import { useDebouncedEffect } from 'builder/hooks/useDebouncedEffect'
import { EditorMobileFooter } from 'builder/components/PreviewPanel'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useResponsiveButtonSize } from 'builder/hooks/useResponsiveButtonSize'
import { Caption, JobPostingContainer, Title } from '../../styles'
import { DEBOUNCE_DELAY, urlRegex } from '../../../constants'
import { ValidationFailIcon, ValidationLoadingIcon, ValidationSuccessIcon } from '../../../styles'
import { JobPostingProps } from '../../types'
import { Container, Content, InputContainer } from './styles'

const translationKey = 'builder.resume_optimizer.job_posting_for_editor_with_tabs.enter_link_step'

const statusIcons = {
  [FetchStatuses.notAsked]: <></>,
  [FetchStatuses.loading]: <ValidationLoadingIcon />,
  [FetchStatuses.loaded]: <ValidationSuccessIcon />,
  [FetchStatuses.failed]: <ValidationFailIcon />,
}

export const EnterLinkStep = (props: JobPostingProps) => {
  const { children, canScrollBottom } = props
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { jobPostingLinkForModal: jobPostingLink } = useTypedSelector(selectors.optimizerSection)
  const resume = useTypedSelector(selectors.resume)
  const { isTablet } = useMediaQueries()

  const [linkValidationStatus, setLinkValidationStatus] = useState<FetchStatuses | null>(() => {
    return jobPostingLink
      ? urlRegex.test(jobPostingLink)
        ? FetchStatuses.loaded
        : FetchStatuses.failed
      : null
  })

  const isValidLink = linkValidationStatus === FetchStatuses.loaded
  const isInvalidLink = linkValidationStatus === FetchStatuses.failed
  const icon = linkValidationStatus && jobPostingLink ? statusIcons[linkValidationStatus] : null
  const buttonSize = useResponsiveButtonSize()

  useEffectOnMount(() => {
    trackInternalEvent('see_resume_optimizer_link_screen')
  })

  useEffect(() => {
    if (jobPostingLink) {
      setLinkValidationStatus(FetchStatuses.loading)
    } else {
      setLinkValidationStatus(FetchStatuses.notAsked)
    }
  }, [jobPostingLink])

  useEffect(() => {
    if (isValidLink) {
      trackInternalEvent('send_job_posting_link', { status: 'ok', link: jobPostingLink })
    } else if (isInvalidLink) {
      trackInternalEvent('send_job_posting_link', { status: 'failed', link: jobPostingLink })
    }
  }, [isValidLink, isInvalidLink, jobPostingLink])

  useDebouncedEffect(
    () => {
      if (jobPostingLink) {
        const validationResult = urlRegex.test(jobPostingLink)
        setLinkValidationStatus(validationResult ? FetchStatuses.loaded : FetchStatuses.failed)
      }
    },
    DEBOUNCE_DELAY,
    [jobPostingLink],
  )

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replaceAll('https://', '')
    const linkValue = inputValue ? 'https://' + inputValue : ''
    dispatch(actions.setJobPostingLinkForModal(linkValue))
  }

  const handleContinueClick = () => {
    if (linkValidationStatus === FetchStatuses.loading) {
      return
    }

    if (!jobPostingLink || linkValidationStatus === FetchStatuses.failed) {
      setLinkValidationStatus(FetchStatuses.failed)
      return
    }

    dispatch(
      actions.checkResumeScore({
        jobPostingLink,
        resumeId: resume?.id,
      }),
    )
    trackInternalEvent('click_continue_job_posting_link')
  }

  const getStatusText = (status: FetchStatuses | null): string => {
    switch (status) {
      case FetchStatuses.failed:
        if (!jobPostingLink) {
          return i18n.t(`${translationKey}.error_messages.empty`)
        }
        return i18n.t(`${translationKey}.error_messages.failed`)
      default:
        return i18n.t(`${translationKey}.input_label`)
    }
  }

  const error = isInvalidLink ? getStatusText(linkValidationStatus) : null
  const status = !isInvalidLink ? getStatusText(linkValidationStatus) : null

  const button = (
    <ButtonV2
      onClick={handleContinueClick}
      size={buttonSize}
      disabled={!!error || linkValidationStatus === FetchStatuses.loading}
    >
      {i18n.t(`${translationKey}.continue`)}
    </ButtonV2>
  )

  return (
    <>
      <JobPostingContainer>
        <Container>
          <Title>{i18n.t(`${translationKey}.title`)}</Title>
          <Content>
            <InputContainer>
              <TextField
                value={jobPostingLink}
                name="link"
                placeholder={i18n.t(`${translationKey}.input_placeholder`)}
                onChange={handleInputChange}
                inputIcon={icon}
                inputIconPosition="right"
                error={error}
                autoFocus={true}
              />
              {status && <Caption>{status}</Caption>}
            </InputContainer>
            {!isTablet && button}
          </Content>
        </Container>
      </JobPostingContainer>
      {children}
      {isTablet && (
        <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>{button}</EditorMobileFooter>
      )}
    </>
  )
}
